import { Fragment, useCallback, memo, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { TrellisEPHeader } from 'editor-components/header/EPHeader';
import EPNavigationBar from 'editor-components/header/EPNavigationBar';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { View } from 'foundations-components/components/View';
import UsageTracker, { useTracker } from 'ContentEditorUI/utils/UsageTracker';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';

// @ts-expect-error not typed yet
import TranslationSelectorContainer from 'ContentEditorUI/containers/TranslationSelectorContainer';
import PageAbControlsContainer from 'page-editor-ui/containers/PageAbControlsContainer';
// @ts-expect-error not typed yet
import PageMABControlsContainer from 'page-editor-ui/containers/PageMABControlsContainer';
import BreakpointsContainer from 'ContentEditorUI/containers/BreakpointsContainer';
import { ExitButton } from 'page-editor-ui/components/ExitButton';
import PagePublishButtonContainer from 'page-editor-ui/containers/PagePublishButtonContainer';
// @ts-expect-error not typed yet
import ExitDeveloperModeButton from 'ContentEditorUI/components/ExitDeveloperModeButton';
import { getHasUnsavedChanges } from 'ContentEditorUI/redux/selectors/saveDataSelectors';
import { getIsInDeveloperMode, getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getHasUndoRedoAccess, getIsEditingGridLayouts } from 'ContentEditorUI/redux/selectors/moduleSelectors';
// @ts-expect-error not typed yet
import { getHasCurrentTranslations } from 'ContentEditorUI/redux/selectors/translationSelectors';
import { getCanUseQuickSpot } from 'ContentEditorUI/redux/selectors/authSelectors';
import { showLoading, hideLoading, openModal
// @ts-expect-error not typed yet
} from 'ContentEditorUI/redux/actions/appActions';
// @ts-expect-error not typed yet
import { save } from 'page-editor-ui/redux/actions/pageSaveActions';
// @ts-expect-error not typed yet
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import { setShouldClearHeaderActiveItem, updateHeaderActiveItem } from 'ContentEditorUI/redux/actions/headerActions';
import { getShouldClearHeaderActiveItem } from 'ContentEditorUI/redux/selectors/headerSelectors';
import EPSettingsButton from 'editor-components/header/EPSettingsButton';
import EPSpotlightButton from 'editor-components/header/EPSpotlightButton';
import UndoRedoButtonContainer from 'ContentEditorUI/containers/UndoRedoButtonContainer';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { getShouldUseResponsiveBreakpoints } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
// @ts-expect-error not typed yet
import { useNavigationGroups } from 'page-editor-ui/hooks/useNavigationGroups';
// @ts-expect-error not typed yet
import PageSaveButtonContainer from './PageSaveButtonContainer';
import { MODAL_TYPES } from 'ContentEditorUI/lib/modalConstants';
import { getContentModelName, getContentModelId } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
// @ts-expect-error not typed yet
import { updateName } from 'ContentEditorUI/redux/actions/baseContentModelActions';
import useSetSettingsModalOpenWithNavItem from 'page-editor-ui/hooks/useSetSettingsModalOpenWithNavItem';
import ContentPublishStatus from 'ContentEditorUI/components/shared/ContentPublishStatus';
import ContentCrmObjectProvider from 'ContentEditorUI/components/publishing/ContentCrmObjectProvider';
import { getIsLandingPage, getIsBlogListingPage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { ContentObjectTypes } from 'content-management-data-lib';
import { getIsUsingPersonalization } from 'page-editor-ui/redux/selectors/pageContentModelSelectors';
import isBranchPreviewFunc from 'editor-components/utils/isBranchPreview';
import { PersonalizationVariantControls } from '../containers/PersonalizationVariantControlsContainer';
import { AutoSizedTextInput } from 'foundations-components/transitional/components/AutoSizedTextInput';
import { getUpdatedThemeFieldKeys } from 'theme-previewer-lib/selectors/themeSettingsMetaSelectors';
import { getCanUseUniversalThemeEditor } from 'page-editor-ui/redux/selectors/authSelectors';
import { useUniversalThemeSave } from 'theme-previewer-lib/hooks/useUniversalThemeSave';
import VariantSelectUpdateWrapper from './VariantSelectUpdateWrapper';
import PersonalizationProvider from 'personalization-lib/components/wrappers/PersonalizationProvider';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EditorToolbarEndSlot = () => {
  const dispatch = useDispatch();
  const hasUnsavedChanges = useSelector(getHasUnsavedChanges);
  const isInDeveloperMode = useSelector(getIsInDeveloperMode);
  const updatedThemeFieldKeys = useSelector(getUpdatedThemeFieldKeys);
  const canUseUniversalThemeEditor = useSelector(getCanUseUniversalThemeEditor);
  const {
    saveThemeSettingsAction,
    getUniversalThemeSupportedLastSave
  } = useUniversalThemeSave();
  const handleSaveClick = useCallback(() => {
    if (canUseUniversalThemeEditor && updatedThemeFieldKeys.length) {
      saveThemeSettingsAction({
        isAutoSave: false,
        fromUniversal: true
      });
    }
    dispatch(save());
    UsageTracker.trackEditorInteraction({
      action: 'Revisions Action',
      event: 'clicked-save'
    });
  }, [dispatch, canUseUniversalThemeEditor, updatedThemeFieldKeys, saveThemeSettingsAction]);
  const handlePreviewClick = useCallback(() => {
    if (hasUnsavedChanges) {
      dispatch(showLoading());
      dispatch(save()).finally(() => {
        dispatch(hideLoading());
        dispatch(routeToPage('preview'));
      }).catch(error => console.error(error));
    } else {
      dispatch(routeToPage('preview'));
    }
  }, [dispatch, hasUnsavedChanges]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isInDeveloperMode && /*#__PURE__*/_jsx(ExitDeveloperModeButton, {}), /*#__PURE__*/_jsx(PageSaveButtonContainer, {
      onSaveClick: handleSaveClick,
      getUniversalThemeSupportedLastSave: getUniversalThemeSupportedLastSave
    }), /*#__PURE__*/_jsx(View, {
      children: /*#__PURE__*/_jsx(Button, {
        "data-onboarding": "ep-navigation-preview",
        "data-campaign-assistant-onboarding-tour": "tour-step-preview",
        onClick: handlePreviewClick,
        use: "secondary-alt",
        size: "medium",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "components.tabBar.preview"
        })
      })
    }), /*#__PURE__*/_jsx(PagePublishButtonContainer, {})]
  });
};
const PatagoniaHeaderContainer = /*#__PURE__*/memo(() => {
  const dispatch = useDispatch();
  const isEditingGridLayouts = useSelector(getIsEditingGridLayouts);
  const hasUndoRedoAccess = useSelector(getHasUndoRedoAccess);
  const isReadOnlyMode = useSelector(getIsReadOnlyMode);
  const shouldUseResponsiveBreakpoints = useSelector(getShouldUseResponsiveBreakpoints);
  const shouldClearHeader = useSelector(getShouldClearHeaderActiveItem);
  const pageName = useSelector(getContentModelName);
  const tracker = useTracker();
  const navigationGroups = useNavigationGroups();
  const canUseQuickSpot = useSelector(getCanUseQuickSpot);
  const hasCurrentTranslations = useSelector(getHasCurrentTranslations);
  const handleQuickSpotOpenClick = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.SPOTLIGHT));
  }, [dispatch]);
  const handleActiveItemChange = useCallback(activeItem => {
    dispatch(updateHeaderActiveItem(activeItem));
    if (activeItem) {
      dispatch(setShouldClearHeaderActiveItem(false));
    }
  }, [dispatch]);
  const setSettingsModalOpenWithNavItem = useSetSettingsModalOpenWithNavItem(MODAL_TYPES.SETTINGS_MODAL);
  const handleChangeName = e => dispatch(updateName(e.target.value));
  const isLandingPage = useSelector(getIsLandingPage);
  const isBlogListingPage = useSelector(getIsBlogListingPage);
  const contentCrmObjectId = useSelector(getContentModelId);
  const isBranchPreview = useMemo(() => isBranchPreviewFunc(), []);
  const isUsingPersonalization = useSelector(getIsUsingPersonalization);
  const contentObjectType = useMemo(() => {
    if (isBlogListingPage) {
      return ContentObjectTypes.BLOG_LISTING_PAGE;
    }
    if (isLandingPage) {
      return ContentObjectTypes.LANDING_PAGE;
    }
    return ContentObjectTypes.SITE_PAGE;
  }, [isBlogListingPage, isLandingPage]);
  return /*#__PURE__*/_jsx(ContentCrmObjectProvider, {
    contentObjectType: contentObjectType,
    contentCrmObjectId: contentCrmObjectId,
    children: /*#__PURE__*/_jsxs(PersonalizationProvider, {
      tracker: tracker,
      appName: "page-editor-ui",
      children: [/*#__PURE__*/_jsx(VariantSelectUpdateWrapper, {}), /*#__PURE__*/_jsx(TrellisEPHeader, {
        tabsStyles: {
          marginBlock: '0'
        },
        isBranchPreview: isBranchPreview,
        titleText: /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          justify: "center",
          direction: "column",
          children: [/*#__PURE__*/_jsx(AutoSizedTextInput, {
            "data-test-id": "page-title",
            affordance: true,
            onChange: handleChangeName,
            use: "on-dark",
            value: pageName,
            readOnly: isReadOnlyMode
          }), /*#__PURE__*/_jsx(ContentPublishStatus, {})]
        }),
        editorToolbarStartSlot: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ExitButton, {
            buttonSize: "medium"
          }), /*#__PURE__*/_jsx(EPNavigationBar, {
            navigationGroups: navigationGroups,
            onActiveItemChange: handleActiveItemChange,
            shouldClear: shouldClearHeader
          }), isEditingGridLayouts && /*#__PURE__*/_jsx(Badge, {
            use: "beta",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "gridLayoutEditor.name"
            })
          })]
        }),
        editorToolbarEndSlot: /*#__PURE__*/_jsx(EditorToolbarEndSlot, {}),
        tabsStartSlot: /*#__PURE__*/_jsxs(List, {
          use: "inline-divided",
          children: [hasCurrentTranslations && /*#__PURE__*/_jsx(ListItem, {
            marginInlineEnd: "50",
            children: /*#__PURE__*/_jsx(TranslationSelectorContainer, {})
          }), /*#__PURE__*/_jsx(PageMABControlsContainer, {}), /*#__PURE__*/_jsx(PageAbControlsContainer, {}), isUsingPersonalization && /*#__PURE__*/_jsx(PersonalizationVariantControls, {})]
        }),
        tabsMiddleSlot: /*#__PURE__*/_jsx(Fragment, {
          children: shouldUseResponsiveBreakpoints && /*#__PURE__*/_jsx(BreakpointsContainer, {})
        }),
        tabsEndSlot: /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          justify: "end",
          children: [canUseQuickSpot && tracker && /*#__PURE__*/_jsx(EPSpotlightButton, {
            onClick: handleQuickSpotOpenClick,
            usageTracker: tracker
          }), tracker && /*#__PURE__*/_jsx(EPSettingsButton, {
            onClick: () => setSettingsModalOpenWithNavItem('general'),
            usageTracker: tracker
          }), /*#__PURE__*/_jsx(NavMarker, {
            name: "PARENT_APP_CHROME_RENDERED"
          }), hasUndoRedoAccess && /*#__PURE__*/_jsx(View, {
            marginInlineStart: "200",
            children: /*#__PURE__*/_jsx(UndoRedoButtonContainer, {
              isVisible: true,
              disabled: isReadOnlyMode
            })
          })]
        })
      })]
    })
  });
});
export default PatagoniaHeaderContainer;